import React, { useState, useEffect } from "react";
import Grids from "./Grids";
import { prepareBarData } from "../../utils/svgUtils";
import Legend from "./LegendHorizontal";
import ResponsiveTable from "./ResponsiveTable";

export default function BarGraph({
    inputData,
    legendLabels,
    rowLabel,
    pixelWidth = 1500,
    pixelHeight = 1000,
}) {
    const [preparedData, setPreparedData] = useState({
        pxWidth: pixelWidth, // recommended be 150% of pxHeight
        pxHeight: pixelHeight,
        opacity: 1, // opacity of bar and legend colors
        legendLabels: legendLabels,
        rowLabel: rowLabel,
        colors: [
            "#ABC6C7", // green
            "#EBD084", // yellow
            "#85B2E4", // blue
            "#BB9BCD", // purple
            "#DB8E8B", // red
            "#ECEDEE", // gray
        ],
        // adjusted automatically depending on the data
        data: [{ label: "", values: [] }],
        widthCoefficient: 0,
        heightCoefficient: 0,
        maxHeight: 0,
    });

    const [showTable, setShowTable] = useState(false);
    const toggleTable = () => setShowTable(!showTable);

    useEffect(() => {
        setPreparedData((prevData) => prepareBarData(prevData, inputData));
    }, [inputData]);

    return (
        <div>
            <svg
                className="bg-white rounded rounded-4 bg-opacity-75"
                viewBox={`0 0 ${preparedData.pxWidth} ${preparedData.pxHeight}`}
            >
                {/* Paints grids and y-axis data */}
                <Grids
                    pxWidth={preparedData.pxWidth}
                    pxHeight={preparedData.pxHeight}
                    maxHeight={preparedData.maxHeight}
                />

                {/* Paints each Bar data */}
                <g transform={`translate(${preparedData.pxWidth * 0.105}, 0)`}>
                    {preparedData.data.map((value, index) => {
                        const heightsWithColors = value.values
                            .map((height, i) => ({
                                height,
                                color: preparedData.colors[i],
                            }))
                            .sort((a, b) => b.height - a.height);

                        return (
                            <g key={index}>
                                {/* Paints each subBar */}
                                {heightsWithColors.map((subValue, subIndex) => {
                                    const x =
                                        preparedData.widthCoefficient * index;
                                    const y =
                                        preparedData.pxHeight * 0.85 -
                                        Math.floor(
                                            subValue.height /
                                                preparedData.heightCoefficient
                                        );
                                    const height = Math.round(
                                        subValue.height /
                                            preparedData.heightCoefficient
                                    );
                                    const width =
                                        preparedData.widthCoefficient * 0.75;
                                    return (
                                        <rect
                                            key={subIndex}
                                            x={x}
                                            y={y}
                                            height={height < 0 ? 0 : height}
                                            width={width}
                                            fill={subValue.color}
                                            opacity={preparedData.opacity}
                                        ></rect>
                                    );
                                })}

                                {/* Paints label text on x-axis */}
                                <text
                                    x={
                                        preparedData.widthCoefficient * index +
                                        preparedData.pxWidth * 0.01
                                    }
                                    y={
                                        preparedData.widthCoefficient * 0.4 +
                                        preparedData.pxHeight * 0.905
                                    }
                                    transform={`translate(0, 10) rotate(-90, ${
                                        preparedData.widthCoefficient * index
                                    }, ${preparedData.pxHeight * 0.9})`}
                                    textAnchor="middle"
                                    fontSize={preparedData.pxHeight * 0.028}
                                >
                                    {value.label}
                                </text>

                                {/* Bar on hover data */}
                                <title>{`${preparedData.rowLabel}: ${
                                    value.label
                                }\n${value.values
                                    .map(
                                        (height, i) =>
                                            `${legendLabels[i]}: ${height}`
                                    )
                                    .join("\n")}`}</title>
                            </g>
                        );
                    })}
                </g>

                {/* Legend */}
                <Legend
                    pxWidth={preparedData.pxWidth}
                    pxHeight={preparedData.pxHeight}
                    labels={preparedData.legendLabels}
                    colors={preparedData.colors}
                    opacity={preparedData.opacity}
                />
            </svg>

            {/* Table data */}
            <div className="d-flex justify-content-center">
                <button
                    className="btn btn-sm main--button mt-3"
                    onClick={toggleTable}
                >
                    {showTable ? "Сховати" : "Детальніше"}
                </button>
            </div>
            {showTable && (
                <div className="mt-3">
                    <ResponsiveTable
                        data={preparedData.data}
                        headLabels={preparedData.legendLabels}
                        rowLabel={preparedData.rowLabel}
                    />
                </div>
            )}
            {showTable && preparedData.data.length > 15 && (
                <div className="d-flex justify-content-center mt-3">
                    <button
                        className="btn btn-sm main--button"
                        onClick={toggleTable}
                    >
                        Сховати
                    </button>
                </div>
            )}
        </div>
    );
}
